import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { TeamsService } from './teams.service';

interface CachedToken {
  accessToken: string;
  /** Expiry as epoch milliseconds. */
  expiresAt: number;
}

/** Refresh a token this many ms before it actually expires. */
const EXPIRY_SKEW_MS = 60_000;

/**
 * Acquires the core API access token when running inside Teams.
 *
 * Flow: Teams SSO token (getSsoToken) -> backend On-Behalf-Of endpoint -> API
 * access token for `environment.apiConfig.scopes`. The OBO is deliberately
 * scoped to the core API ONLY — no Microsoft Graph or other downstream tokens
 * are ever requested. The token is cached until it is near expiry.
 *
 * Uses `fetch` (not HttpClient) for the exchange so it bypasses the Angular
 * interceptors — otherwise the teamsAuthInterceptor would try to authorize the
 * OBO request itself and recurse.
 */
@Injectable({ providedIn: 'root' })
export class TeamsAuthService {
  private readonly teams = inject(TeamsService);
  private cached: CachedToken | null = null;
  /** De-dupes concurrent exchanges. */
  private inFlight: Promise<string> | null = null;

  /** Core API access token (api://.../access_as_user), via Teams SSO -> OBO. */
  getApiToken(): Promise<string> {
    if (this.cached && this.cached.expiresAt - EXPIRY_SKEW_MS > this.now()) {
      return Promise.resolve(this.cached.accessToken);
    }
    if (this.inFlight) return this.inFlight;

    this.inFlight = this.exchange()
      .then((token) => {
        this.cached = token;
        return token.accessToken;
      })
      .finally(() => {
        this.inFlight = null;
      });
    return this.inFlight;
  }

  private async exchange(): Promise<CachedToken> {
    const ssoToken = await this.teams.getSsoToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${ssoToken}`,
    };
    const subscriptionKey = environment.apiConfig.ocpApimSubscriptionKey;
    if (subscriptionKey) {
      headers['Ocp-Apim-Subscription-Key'] = subscriptionKey;
    }

    const response = await fetch(this.oboEndpoint(), {
      method: 'POST',
      headers,
      // Always the core API scope — never Graph or any downstream resource.
      body: JSON.stringify({ scopes: environment.apiConfig.scopes }),
    });

    if (!response.ok) {
      throw new Error(
        `Teams OBO exchange failed (${response.status} ${response.statusText})`
      );
    }

    // Accept both Entra's native token response (access_token / expires_in) and
    // a reshaped { accessToken, expiresOn } — whichever the OBO layer returns.
    const data: {
      access_token?: string;
      accessToken?: string;
      expires_in?: number; // seconds from now (Entra native)
      expiresOn?: string | number; // absolute time (reshaped)
    } = await response.json();

    const accessToken = data.access_token ?? data.accessToken;
    if (!accessToken) {
      throw new Error('Teams OBO exchange returned no access token');
    }

    return {
      accessToken,
      expiresAt: this.resolveExpiry(data),
    };
  }

  private resolveExpiry(data: {
    expires_in?: number;
    expiresOn?: string | number;
  }): number {
    // Entra returns expires_in as seconds-from-now.
    if (typeof data.expires_in === 'number') {
      return this.now() + data.expires_in * 1000;
    }
    return this.parseExpiry(data.expiresOn);
  }

  private oboEndpoint(): string {
    const configured = environment.teams?.oboTokenEndpoint;
    if (configured) return configured;
    const base = environment.apiConfig.serverBaseUrl.replace(/\/$/, '');
    return `${base}/auth/teams-obo`;
  }

  private parseExpiry(expiresOn: string | number | undefined): number {
    if (typeof expiresOn === 'number') {
      // Heuristic: seconds-since-epoch vs ms-since-epoch.
      return expiresOn < 1e12 ? expiresOn * 1000 : expiresOn;
    }
    if (typeof expiresOn === 'string') {
      const parsed = Date.parse(expiresOn);
      if (!Number.isNaN(parsed)) return parsed;
    }
    // Fall back to a conservative 5-minute lifetime.
    return this.now() + 5 * 60_000;
  }

  private now(): number {
    return Date.now();
  }
}
